import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import axios, { CancelToken } from "axios";
import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { Select } from "components/select/";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import SelectNew from "components/select/SelectNew";
import {
  GetTeeSheetNotificationTemplate,
  PostTeeSheetNotification,
  TTeesheetNotificationTemplate,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";
import TextEditor from "components/textEditor/textEditor";
import { isEmpty, valueToString } from "helpers/Helpers";
import Popup from "components/popup/Popup";

interface IValidatedField<T> {
  value: T;
  isValid: boolean;
  isDirty: boolean;
}

interface ITeeSheetNotificationNewState {
  startingDate: Date;
  endingDate: Date;
  courseId: number;
  userType: IValidatedField<TTeesheetNotificationType | undefined>;
  subject: IValidatedField<string>;
  content: IValidatedField<string>;
  occurrences: IValidatedField<TOccurrence[]>;
}

type TOccurrence =
  | "everyday"
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

export default function TeeSheetNotificationNew() {
  const history = useHistory();
  const { t } = useTranslation();

  const NO_COURSE_ID = -1;
  const NO_TEMPLATE_ID = -1;

  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);

  function areSameCalendarDay(leftDate: Date, rightDate: Date) {
    return (
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate()
    );
  }

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("Internal"),
        value: "admin",
      },
      {
        label: t("External"),
        value: "customer",
      },
    ];
  }, []);

  const occurrenceOptions = useMemo(() => {
    const options: Array<{ value: TOccurrence; label: string }> = [
      { value: "monday", label: t("Every Monday") },
      { value: "tuesday", label: t("Every Tuesday") },
      { value: "wednesday", label: t("Every Wednesday") },
      { value: "thursday", label: t("Every Thursday") },
      { value: "friday", label: t("Every Friday") },
      { value: "saturday", label: t("Every Saturday") },
      { value: "sunday", label: t("Every Sunday") },
      { value: "everyday", label: t("Everyday") },
    ];
    return options;
  }, []);

  const [state, setState] = useState<ITeeSheetNotificationNewState>({
    startingDate: new Date(),
    endingDate: new Date(),
    courseId: NO_COURSE_ID,
    userType: { value: undefined, isValid: false, isDirty: false },
    subject: { value: "", isValid: false, isDirty: false },
    content: { value: "", isValid: false, isDirty: false },
    occurrences: { value: [], isValid: true, isDirty: false },
  });

  const [notificationTemplates, setNotificationTemplates] = useState<TTeesheetNotificationTemplate[]>(undefined);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(NO_TEMPLATE_ID);
  const [pendingTemplateId, setPendingTemplateId] = useState<number>(NO_TEMPLATE_ID);
  const [templateOverridePopupVisible, setTemplateOverridePopupVisible] = useState<boolean>(false);
  const [templateResetPopupVisible, setTemplateResetPopupVisible] = useState<boolean>(false);
  const [templateSelectRenderKey, setTemplateSelectRenderKey] = useState<number>(0);
  const ignoredTemplateChangeRef = useRef<number | undefined>(NO_TEMPLATE_ID);

  useEffect(() => {
    if (facilityStore.facility) {
      setState(prev => ({
        ...prev,
        courseId: facilityStore.facility.courses?.[0]?.id,
      }));
    }
  }, [facilityStore.facility]);

  const isDateRangeSelected = useMemo(() => {
    return !areSameCalendarDay(state.startingDate, state.endingDate);
  }, [state.startingDate, state.endingDate]);

  function toggleOccurrence(occurrenceValue: TOccurrence) {
    const selectedOccurrences = state.occurrences.value ? [...state.occurrences.value] : [];

    const isAlreadySelected = selectedOccurrences.includes(occurrenceValue);
    if (isAlreadySelected) {
      const nextOccurrences = selectedOccurrences.filter(value => value !== occurrenceValue);
      setState(prev => ({
        ...prev,
        occurrences: { value: nextOccurrences, isValid: nextOccurrences.length > 0, isDirty: true },
      }));
      return;
    }

    // Mirror booking behavior: "everyday" is mutually exclusive with weekdays.
    let nextOccurrences: TOccurrence[] = selectedOccurrences;
    const isEverydaySelected = selectedOccurrences.includes("everyday");
    if (occurrenceValue === "everyday" && selectedOccurrences.length > 0) {
      nextOccurrences = ["everyday"];
    } else if (occurrenceValue !== "everyday" && isEverydaySelected) {
      nextOccurrences = [occurrenceValue];
    } else {
      nextOccurrences = [...selectedOccurrences, occurrenceValue];
    }

    setState(prev => ({
      ...prev,
      occurrences: { value: nextOccurrences, isValid: nextOccurrences.length > 0, isDirty: true },
    }));
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadNotificationTemplates(source.token);
    return () => source.cancel();
  }, []);

  async function loadNotificationTemplates(token?: CancelToken) {
    if (notificationTemplates !== undefined) {
      setNotificationTemplates(undefined);
    }

    const response = await GetTeeSheetNotificationTemplate({}, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (response.status !== StatusCode.OK || !Array.isArray(response.data)) {
      dispatch(showError(t("Error loading notification templates.")));
      setNotificationTemplates([]);
      return;
    }

    setNotificationTemplates(response.data);
  }

  function applyTemplate(templateId: number) {
    const template = notificationTemplates?.find(item => item.id === templateId);
    if (!template) {
      return;
    }

    ignoredTemplateChangeRef.current = templateId;
    setSelectedTemplateId(templateId);
    setState(previousState => ({
      ...previousState,
      userType: { value: template.user_type, isValid: true, isDirty: true },
      subject: { value: template.subject ?? "", isValid: true, isDirty: true },
      content: { value: template.content ?? "", isValid: true, isDirty: true },
    }));
  }

  function clearTemplateValues() {
    ignoredTemplateChangeRef.current = NO_TEMPLATE_ID;
    setSelectedTemplateId(NO_TEMPLATE_ID);
    setState(previousState => ({
      ...previousState,
      userType: { value: undefined, isValid: false, isDirty: false },
      subject: { value: "", isValid: false, isDirty: false },
      content: { value: "", isValid: false, isDirty: false },
    }));
  }

  function hasNotificationContentEntered() {
    const hasSubject = state.subject.value.trim() !== "";
    const hasContent = state.content.value.trim() !== "";
    return hasSubject && hasContent;
  }

  function hasHeaderOrContentEntered() {
    const hasSubject = state.subject.value.trim() !== "";
    const hasContent = state.content.value.trim() !== "";
    return hasSubject || hasContent;
  }

  function handleTemplateChange(templateId: number) {
    if (ignoredTemplateChangeRef.current === templateId) {
      ignoredTemplateChangeRef.current = undefined;
      return;
    }

    if (templateId === NO_TEMPLATE_ID) {
      if (hasHeaderOrContentEntered()) {
        setPendingTemplateId(NO_TEMPLATE_ID);
        setTemplateOverridePopupVisible(true);
        return;
      }

      clearTemplateValues();
      return;
    }

    if (hasNotificationContentEntered()) {
      setPendingTemplateId(templateId);
      setTemplateOverridePopupVisible(true);
      return;
    }

    applyTemplate(templateId);
  }

  function confirmTemplateOverride() {
    const templateIdToApply = pendingTemplateId;
    setTemplateOverridePopupVisible(false);
    setPendingTemplateId(NO_TEMPLATE_ID);

    if (templateIdToApply === NO_TEMPLATE_ID) {
      clearTemplateValues();
      return;
    }

    applyTemplate(templateIdToApply);
  }

  async function postNotification() {
    setState(prev => ({
      ...prev,
      userType: { ...prev.userType, isDirty: true },
      subject: { ...prev.subject, isDirty: true },
      content: { ...prev.content, isDirty: true },
      occurrences: isDateRangeSelected
        ? { ...prev.occurrences, isDirty: true, isValid: prev.occurrences.value?.length > 0 }
        : prev.occurrences,
    }));

    const occurrencesAreValid = isDateRangeSelected ? state.occurrences.value?.length > 0 : true;
    if (!state.userType.isValid || !state.subject.isValid || !state.content.isValid || !occurrencesAreValid) {
      return;
    }

    const postNotificationRes = await PostTeeSheetNotification(
      {
        start_date: state.startingDate.toISOString().slice(0, 10),
        end_date: state.endingDate.toISOString().slice(0, 10),
        course_id: state.courseId === NO_COURSE_ID ? undefined : state.courseId,
        subject: state.subject.value,
        user_type: state.userType.value,
        type: undefined,
        content: state.content.value,
        occurrences: isDateRangeSelected ? state.occurrences.value : undefined,
      },
      true,
    );

    if (postNotificationRes.status !== StatusCode.OK) {
      dispatch(showError(postNotificationRes.data as string));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification");
  }

  const createIsDisabled =
    !state.userType.isValid ||
    !state.subject.isValid ||
    !state.content.isValid ||
    (isDateRangeSelected && state.occurrences.value.length === 0);

  const primaryAction = {
    content: t("Create"),
    action: postNotification,
    disabled: createIsDisabled,
  };

  const secondaryActions = [
    {
      content: t("Reset Template"),
      disabled: selectedTemplateId === NO_TEMPLATE_ID,
      action: () => setTemplateResetPopupVisible(true),
    },
  ];

  return (
    <Page
      title={t("New Tee Sheet Notification")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to notifications"),
          url: "/admin/settings/tee-sheet/notification",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                label={t("Notification Date Range")}
                months={1}
                position="left"
                startingDate={state.startingDate}
                setStartingDate={(updatedStartingDate: Date) => {
                  const willBeRange = !areSameCalendarDay(updatedStartingDate, state.endingDate);
                  setState(prev => ({
                    ...prev,
                    startingDate: updatedStartingDate,
                    occurrences: willBeRange ? prev.occurrences : { value: [], isValid: true, isDirty: false },
                  }));
                }}
                endingDate={state.endingDate}
                setEndingDate={(updatedEndingDate: Date) => {
                  const willBeRange = !areSameCalendarDay(state.startingDate, updatedEndingDate);
                  setState(prev => ({
                    ...prev,
                    endingDate: updatedEndingDate,
                    occurrences: willBeRange ? prev.occurrences : { value: [], isValid: true, isDirty: false },
                  }));
                }}
                helpText="The date range for which this notification will be active."
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                key={`template-${String(selectedTemplateId)}-${String(templateSelectRenderKey)}`}
                label={t("Notification Template")}
                onChange={(templateId: number) => handleTemplateChange(templateId)}
                defaultValue={selectedTemplateId}
                loading={notificationTemplates === undefined}
                helpText="Selecting a template will prefill user type, header, and contents. You can still edit those fields."
              >
                <Select.Option value={NO_TEMPLATE_ID} name={t("None")}>
                  {t("None")}
                </Select.Option>
                {notificationTemplates?.map(template => (
                  <Select.Option key={template.id} value={template.id} name={template.subject}>
                    {template.subject}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>

            {isDateRangeSelected && (
              <FormLayout.Group>
                <SelectNew
                  id="notification_occurrences"
                  label={t("Occurrence")}
                  placeholder={
                    state.occurrences.value?.map(occurrence => valueToString(occurrence))?.join(", ") ||
                    t("Select occurrence")
                  }
                  onChange={occurrenceValue => toggleOccurrence(occurrenceValue)}
                  multiValue={state.occurrences.value ?? []}
                  error={!state.occurrences.isValid && state.occurrences.isDirty}
                  options={occurrenceOptions.map(option => ({
                    id: option.value,
                    label: option.label,
                  }))}
                  helpText="Choose which days within the date range this notification should appear on. Selecting 'Everyday' will create a notification for every day in the range; selecting specific weekdays will only create notifications on those days."
                />
              </FormLayout.Group>
            )}

            {facilityStore.facility?.courses?.length > 1 && (
              <FormLayout.Group>
                <Select
                  label={t("Is this notification specific to a course?")}
                  onChange={(courseId: number) => {
                    setState(prev => ({ ...prev, courseId }));
                  }}
                  defaultValue={state.courseId}
                >
                  <Select.Option value={NO_COURSE_ID} name={undefined}>
                    &nbsp;
                  </Select.Option>
                  {facilityStore.facility?.courses.map(course => {
                    return (
                      <Select.Option key={course.id} value={course.id} name={course.full_name}>
                        {course.full_name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
            )}
            <FormLayout.Group>
              <Select
                label={t("User Type")}
                onChange={value =>
                  setState(prev => ({ ...prev, userType: { ...prev.userType, value, isValid: true, isDirty: true } }))
                }
                defaultValue={state.userType.value}
                error={!state.userType.isValid && state.userType.isDirty}
                helpText="Selecting 'Internal' will show this notification on your internal tee sheet. Selecting 'External' will show this notification on the customer tee sheet."
              >
                {notificationUserTypeOptions.map((option, index: number) => {
                  return (
                    <Select.Option key={index} value={option.value} name={option.label}>
                      {option.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={state.subject.value}
                label={t("Header")}
                onChange={e =>
                  setState(prev => ({
                    ...prev,
                    subject: {
                      ...prev.subject,
                      value: e.target.value,
                      isValid: e.target.value !== "",
                      isDirty: true,
                    },
                  }))
                }
                error={!state.subject.isValid && state.subject.isDirty}
                placeholder={t("Header")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextEditor
                markdownText={state.content.value || ""}
                markdownTextOnChange={newContent =>
                  setState(prev => ({
                    ...prev,
                    content: {
                      ...prev.content,
                      value: newContent,
                      isValid: newContent !== "",
                      isDirty: true,
                    },
                  }))
                }
                label={t("Contents")}
                error={isEmpty(state.content)}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Popup
        onCancel={() => {
          setPendingTemplateId(NO_TEMPLATE_ID);
          setTemplateOverridePopupVisible(false);
          ignoredTemplateChangeRef.current = selectedTemplateId;
          setTemplateSelectRenderKey(previousKey => previousKey + 1);
        }}
        onOk={confirmTemplateOverride}
        cancelText={t("Cancel")}
        okText={t("Apply Template")}
        open={templateOverridePopupVisible}
        type="warning"
        title={t("Replace notification details?")}
        description={t("Applying a template will replace the current user type, header, and contents.")}
      />

      <Popup
        onCancel={() => setTemplateResetPopupVisible(false)}
        onOk={() => {
          setTemplateResetPopupVisible(false);
          applyTemplate(selectedTemplateId);
        }}
        cancelText={t("Cancel")}
        okText={t("Reset")}
        open={templateResetPopupVisible}
        type="warning"
        title={t("Reset to template?")}
        description={t("This will replace user type, header, and contents with the selected template values.")}
      />
    </Page>
  );
}
